import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Pagination } from '@mui/material';
import api from '../services/api';
import { downloadFile } from '../components/ImportProducts';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

const empty = { action: '', section: '', product: '', user_id: '', date_from: '', date_to: '' };
const actions = { asset_created: 'Cadastro', asset_updated: 'Alteração', asset_assigned: 'Atribuição',
  asset_returned: 'Devolução', asset_transferred: 'Transferência', asset_deleted: 'Exclusão',
  inventory_movement_created: 'Entrada/saída de estoque', inventory_movement_updated: 'Alteração de estoque' };
const sections = { ai_license: 'Licenças de IA', physical: 'Ativos físicos', token: 'Tokens', license: 'Licenças', certificate: 'Certificados', domain: 'Domínios' };
const parameters = (filters) => Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '').map(([key, value]) =>
  [key, key.startsWith('date_') ? new Date(value).toISOString() : value]));

export default function AuditLogs() {
  const { user } = useAuth();
  const [filters, setFilters] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = async (values = applied, page = 1) => {
    setBusy(true); setError('');
    try {
      const response = await api.get('/audit/logs', { params: { ...parameters(values), page, per_page: 20 } });
      setLogs(response.data.logs); setPagination(response.data.pagination); setApplied(values);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  useEffect(() => { if (user?.role === 'admin') load(empty); }, [user?.role]);
  const exportRows = async () => {
    setBusy(true); setError('');
    try {
      const response = await api.get('/audit/logs/export', { params: parameters(applied), responseType: 'blob' });
      downloadFile(response.data, 'movimentacoes.csv');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  if (user?.role !== 'admin') return <Layout><Alert severity="warning">Acesso restrito a administradores.</Alert></Layout>;
  return <Layout>
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4">Movimentações de produtos</Typography>
        <Button variant="outlined" disabled={busy} onClick={exportRows}>Exportar CSV</Button>
      </Stack>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {Object.entries(empty).map(([key]) => <TextField key={key} sx={{ minWidth: 170, flex: '1 1 170px' }}
            select={['action', 'section'].includes(key)} type={key.startsWith('date_') ? 'datetime-local' : key === 'user_id' ? 'number' : 'text'}
            label={{ action: 'Movimentação', section: 'Seção', product: 'Produto', user_id: 'ID do autor', date_from: 'De (hora local)', date_to: 'Até (hora local)' }[key]}
            value={filters[key]} InputLabelProps={key.startsWith('date_') ? { shrink: true } : undefined}
            onChange={(event) => setFilters((previous) => ({ ...previous, [key]: event.target.value }))}>
            {['action', 'section'].includes(key) && [<MenuItem key="all" value="">Todos</MenuItem>,
              ...Object.entries(key === 'action' ? actions : sections).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)]}
          </TextField>)}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={busy} onClick={() => load(filters)}>Filtrar</Button>
          <Button disabled={busy} onClick={() => { setFilters(empty); load(empty); }}>Limpar</Button>
        </Stack>
      </Paper>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" sx={{ mb: 1 }} aria-live="polite">{busy ? 'Carregando…' : `${pagination.total} ${pagination.total === 1 ? 'movimentação' : 'movimentações'}`}</Typography>
      <TableContainer component={Paper} sx={{ maxWidth: '100%' }}><Table size="small" sx={{ minWidth: 700 }}><TableHead><TableRow>
        {['Data', 'Autor', 'Produto', 'Movimentação', 'Origem', 'Alterações'].map((label) => <TableCell key={label}>{label}</TableCell>)}
      </TableRow></TableHead><TableBody>
        {!busy && !logs.length && <TableRow><TableCell colSpan={6}>Nenhuma movimentação encontrada.</TableCell></TableRow>}
        {logs.map((log) => {
          const detail = log.details || {};
          return <TableRow key={log.id}>
            <TableCell>{new Date(`${log.created_at}Z`).toLocaleString('pt-BR')}</TableCell>
            <TableCell>{log.user_name || `#${log.user_id || '—'}`}</TableCell>
            <TableCell>{detail.product_name || `${log.resource_type} #${log.resource_id}`}</TableCell>
            <TableCell>{actions[log.action] || log.action}</TableCell>
            <TableCell>{detail.version === 2 ? detail.source === 'import' ? 'Importação' : 'Manual' : 'Legado'}</TableCell>
            <TableCell>{detail.version !== 2 ? 'Dados anteriores indisponíveis' : Object.entries(detail.changes || {}).map(([key, value]) =>
              <Box key={key}>{key}: {String(value.before ?? '—')} → {String(value.after ?? '—')}</Box>)}</TableCell>
          </TableRow>;
        })}
      </TableBody></Table></TableContainer>
      <Pagination sx={{ mt: 2 }} disabled={busy} page={pagination.page} count={Math.max(1, pagination.pages)} onChange={(_, page) => load(applied, page)} />
    </Box>
  </Layout>;
}
